import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Calendar, Clock, MapPin, Users, Video, Building2, ArrowLeft, Heart,
  MessageCircle, Send, Check, UserPlus, Crown, ChevronDown, ChevronUp, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

const EventPostCard = ({ post, onLike, onComment }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(post.id, commentText.trim());
    setCommentText('');
    setSubmitting(false);
    setShowComments(true);
  };

  return (
    <Card className="overflow-hidden" data-testid={`event-post-${post.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {post.user_photo ? (
              <img src={resolveImageUrl(post.user_photo)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-sm font-bold text-primary">{(post.user_name || '?')[0]}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{post.user_name}</p>
            <p className="text-[11px] text-muted-foreground">{post.user_profession} · {timeAgo(post.created_at)}</p>
          </div>
        </div>
        <p className="text-sm leading-relaxed mb-4 whitespace-pre-wrap">{post.content}</p>
        <div className="flex items-center gap-4 pt-3 border-t border-border/50">
          <button
            onClick={() => onLike(post.id)}
            className={`flex items-center gap-1.5 text-xs transition-colors ${post.liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-400'}`}
            data-testid={`like-event-post-${post.id}`}
          >
            <Heart className={`w-4 h-4 ${post.liked ? 'fill-current' : ''}`} />
            <span>{post.like_count || 0}</span>
          </button>
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid={`toggle-event-comments-${post.id}`}
          >
            <MessageCircle className="w-4 h-4" />
            <span>{post.comment_count || 0}</span>
            {showComments ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
        {showComments && (
          <div className="mt-3 pt-3 border-t border-border/30 space-y-3">
            {(post.comments || []).map(c => (
              <div key={c.id} className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {c.user_photo ? (
                    <img src={resolveImageUrl(c.user_photo)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] font-bold text-muted-foreground">{(c.user_name || '?')[0]}</span>
                  )}
                </div>
                <div className="flex-1 bg-muted/50 rounded-lg px-3 py-2">
                  <p className="text-xs font-semibold">{c.user_name} <span className="font-normal text-muted-foreground">· {timeAgo(c.created_at)}</span></p>
                  <p className="text-xs mt-0.5">{c.content}</p>
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <Input
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Write a comment..."
                className="text-xs h-8"
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleComment()}
                data-testid={`event-comment-input-${post.id}`}
              />
              <Button size="sm" className="h-8 px-3" disabled={submitting || !commentText.trim()} onClick={handleComment} data-testid={`submit-event-comment-${post.id}`}>
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function EventDetailPage() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [eventData, postsData] = await Promise.all([
        api.getEvent(eventId),
        api.getEventPosts(eventId),
      ]);
      setEvent(eventData);
      setPosts(postsData.posts || []);
    } catch (err) {
      toast.error('Failed to load event');
      navigate('/events');
    } finally { setLoading(false); }
  }, [eventId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreatePost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    try {
      const post = await api.createEventPost(eventId, newPost.trim());
      setPosts([post, ...posts]);
      setNewPost('');
      toast.success('Post created!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to post');
    } finally { setPosting(false); }
  };

  const handleLike = async (postId) => {
    try {
      const result = await api.likeEventPost(eventId, postId);
      setPosts(posts.map(p => p.id === postId ? {
        ...p, liked: result.liked, like_count: result.liked ? (p.like_count || 0) + 1 : Math.max(0, (p.like_count || 0) - 1)
      } : p));
    } catch { toast.error('Failed'); }
  };

  const handleComment = async (postId, content) => {
    try {
      const comment = await api.commentEventPost(eventId, postId, content);
      setPosts(posts.map(p => p.id === postId ? {
        ...p, comments: [...(p.comments || []), comment], comment_count: (p.comment_count || 0) + 1
      } : p));
    } catch { toast.error('Failed to comment'); }
  };

  const handleRSVP = async () => {
    try {
      const result = await api.rsvpEvent(eventId);
      toast.success(result.rsvp ? 'RSVP confirmed!' : 'RSVP cancelled');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-4">
          <div className="h-56 bg-muted rounded-xl" />
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-32 bg-muted rounded-xl" />
        </div>
      </Layout>
    );
  }

  if (!event) return null;

  const isAttendee = event.user_rsvp;
  const isCreator = event.creator_id === user?.id;
  const attendees = event.rsvps || [];
  const attendeeCount = event.rsvp_count || attendees.length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="event-detail-page">
        <Button variant="ghost" size="sm" onClick={() => navigate('/events')} className="gap-1.5" data-testid="back-to-events">
          <ArrowLeft className="w-4 h-4" /> Back to Events
        </Button>

        {/* Cover Header */}
        <div className="rounded-xl overflow-hidden relative">
          <div className="h-56 relative">
            {event.image_url ? (
              <img src={resolveImageUrl(event.image_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 flex items-center justify-center">
                <Calendar className="w-20 h-20 text-white/10" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
            <div className="absolute bottom-5 left-6 right-6">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={event.event_type === 'virtual' ? 'bg-blue-500/80 text-white border-0' : 'bg-green-500/80 text-white border-0'}>
                  {event.event_type === 'virtual' ? <Video className="w-2.5 h-2.5 mr-1" /> : <Building2 className="w-2.5 h-2.5 mr-1" />}
                  {event.event_type === 'virtual' ? 'Virtual' : 'In-Person'}
                </Badge>
                <Badge variant="outline" className="bg-white/15 text-white text-[10px] border-white/20">
                  <Calendar className="w-2.5 h-2.5 mr-1" />{formatDate(event.event_date)}
                </Badge>
              </div>
              <h1 className="text-3xl font-bold text-white drop-shadow-lg">{event.title}</h1>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-sm text-white/80 flex items-center gap-1.5"><Users className="w-4 h-4" />{attendeeCount} attendees</span>
                {event.event_time && <span className="text-sm text-white/60 flex items-center gap-1.5"><Clock className="w-4 h-4" />{event.event_time} {event.timezone}</span>}
                {event.location && <span className="text-sm text-white/60 flex items-center gap-1.5"><MapPin className="w-4 h-4" />{event.location}</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-5">
            {/* About */}
            <Card>
              <CardContent className="p-5">
                <h3 className="font-semibold text-sm mb-2">About this event</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {event.description || 'A great networking event for professionals to connect.'}
                </p>
                {event.link && (
                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Video className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Virtual Meeting</span>
                      </div>
                      <a href={event.link} target="_blank" rel="noopener noreferrer" data-testid="join-meeting-btn">
                        <button className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
                          <ExternalLink className="w-3.5 h-3.5" />Join Meeting
                        </button>
                      </a>
                    </div>
                    <p className="text-[11px] text-blue-600/70 dark:text-blue-400/70 mt-1.5 truncate">{event.link}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Post Composer */}
            {(isAttendee || isCreator) ? (
              <Card data-testid="event-post-composer">
                <CardContent className="p-5">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {user?.profile_photo_url ? (
                        <img src={resolveImageUrl(user.profile_photo_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-bold text-primary">{(user?.full_name || 'U')[0]}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={newPost}
                        onChange={e => setNewPost(e.target.value)}
                        placeholder="Share your thoughts about this event..."
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                        rows={3}
                        data-testid="event-post-input"
                      />
                      <div className="flex justify-end mt-2">
                        <Button size="sm" disabled={posting || !newPost.trim()} onClick={handleCreatePost} data-testid="submit-event-post">
                          <Send className="w-3.5 h-3.5 mr-1.5" />{posting ? 'Posting...' : 'Post'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <Calendar className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">RSVP to this event to join the discussion</p>
                  <Button size="sm" onClick={handleRSVP} data-testid="rsvp-to-post">
                    <UserPlus className="w-4 h-4 mr-1.5" />RSVP Now
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Posts Feed */}
            <div className="space-y-4" data-testid="event-posts-feed">
              {posts.length > 0 ? posts.map(post => (
                <EventPostCard key={post.id} post={post} onLike={handleLike} onComment={handleComment} />
              )) : (
                <Card>
                  <CardContent className="py-10 text-center">
                    <MessageCircle className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">No discussions yet. {(isAttendee || isCreator) ? 'Start the conversation!' : 'RSVP to join.'}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* RSVP Action */}
            <Card>
              <CardContent className="p-5">
                {isAttendee ? (
                  <Button variant="outline" className="w-full text-green-600 border-green-600" onClick={handleRSVP} data-testid="rsvp-sidebar">
                    <Check className="w-4 h-4 mr-2" />Registered · Cancel
                  </Button>
                ) : (
                  <Button className="w-full" onClick={handleRSVP} data-testid="rsvp-sidebar">
                    <UserPlus className="w-4 h-4 mr-2" />RSVP Now
                  </Button>
                )}
                {event.max_attendees && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>{attendeeCount} attending</span>
                      <span>{event.max_attendees - attendeeCount} spots left</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (attendeeCount / event.max_attendees) * 100)}%` }} />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Event Host */}
            <Card data-testid="event-host-card">
              <CardContent className="p-5">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-1.5">
                  <Crown className="w-4 h-4 text-amber-500" /> Event Host
                </h3>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                    {event.creator_photo ? (
                      <img src={resolveImageUrl(event.creator_photo)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-lg font-bold text-primary">{(event.creator_name || '?')[0]}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{event.creator_name}</p>
                    <p className="text-[11px] text-muted-foreground">{event.creator_profession}</p>
                  </div>
                </div>
                {!isCreator && (
                  <Link to="/network">
                    <Button variant="outline" size="sm" className="w-full text-xs" data-testid="connect-host">
                      <UserPlus className="w-3.5 h-3.5 mr-1.5" />Connect with {event.creator_name?.split(' ')[0]}
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>

            {/* Attendees */}
            <Card data-testid="event-attendees-sidebar">
              <CardContent className="p-5">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-primary" /> Attendees ({attendeeCount})
                </h3>
                <div className="space-y-2.5">
                  {attendees.slice(0, 10).map((a, i) => (
                    <div key={a.user_id || i} className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {a.photo ? (
                          <img src={resolveImageUrl(a.photo)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[10px] font-bold text-primary">{(a.name || '?')[0]}</span>
                        )}
                      </div>
                      <p className="text-xs font-medium truncate flex-1">{a.name}</p>
                    </div>
                  ))}
                  {attendeeCount > 10 && (
                    <p className="text-xs text-muted-foreground text-center pt-1">+{attendeeCount - 10} more attendees</p>
                  )}
                  {attendees.length === 0 && (
                    <p className="text-xs text-muted-foreground">No attendees yet. Be the first!</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Event Details */}
            <Card>
              <CardContent className="p-5 space-y-3">
                <h3 className="font-semibold text-sm">Event Details</h3>
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>{formatDate(event.event_date)}</span>
                  </div>
                  {event.event_time && (
                    <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{event.event_time} {event.timezone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                    {event.event_type === 'virtual' ? <Video className="w-4 h-4 text-blue-500 flex-shrink-0" /> : <Building2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
                    <span>{event.location || (event.event_type === 'virtual' ? 'Online Event' : 'Location TBD')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
